import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { AlertTriangle, Upload, X, Globe, RefreshCw, ShieldCheck, ShieldAlert, MailWarning, Loader2 } from "lucide-react";

const COLOR_KEYS = ["navy", "blue", "cyan", "muted", "canvas", "border"];
const ASSET_SLOTS = [
  { kind: "logo_primary", label: "Primary logo (light bg)" },
  { kind: "logo_reversed", label: "Reversed logo (dark bg)" },
  { kind: "logo_symbol", label: "Icon-only symbol" },
  { kind: "favicon", label: "Favicon (32×32)" },
  { kind: "email_header_banner", label: "Email header (600×160)" },
];

function absoluteBrandUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}${url}`;
}

export function BrandingConfigPanel({ tenant, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [theme, setTheme] = useState({});          // raw stored theme
  const [resolved, setResolved] = useState(null);  // full resolved

  const [product, setProduct] = useState("");
  const [tagline, setTagline] = useState("");
  const [colors, setColors] = useState({ ...Object.fromEntries(COLOR_KEYS.map((k) => [k, ""])) });
  const [fontBody, setFontBody] = useState("");
  const [fontDisplay, setFontDisplay] = useState("");
  const [webfontUrls, setWebfontUrls] = useState("");
  const [fromName, setFromName] = useState("");

  const [customDomain, setCustomDomain] = useState(null);
  const [pendingHost, setPendingHost] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [t, cd] = await Promise.all([
        api.get(`/master/tenants/${tenant.id}/theme`),
        api.get(`/master/tenants/${tenant.id}/custom-domain`),
      ]);
      const th = t.data.theme || {};
      setTheme(th);
      setResolved(t.data.resolved);
      setProduct(th.product_display_name || "");
      setTagline(th.tagline || "");
      setColors({
        ...Object.fromEntries(COLOR_KEYS.map((k) => [k, (th.colors && th.colors[k]) || ""])),
      });
      setFontBody(th.font_family_body || "");
      setFontDisplay(th.font_family_display || "");
      setWebfontUrls((th.webfont_urls || []).join("\n"));
      setFromName(th.email_from_name || "");
      setCustomDomain(cd.data);
      setPendingHost(cd.data.custom_domain || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      patch.product_display_name = product || null;
      patch.tagline = tagline || null;
      patch.font_family_body = fontBody || null;
      patch.font_family_display = fontDisplay || null;
      patch.email_from_name = fromName || null;
      // Colors: only non-empty go into the colors sub-doc.
      const colorPatch = {};
      let anyColor = false;
      for (const k of COLOR_KEYS) {
        colorPatch[k] = colors[k] || null;
        if (colors[k]) anyColor = true;
      }
      patch.colors = anyColor ? colorPatch : null;
      // Webfont URLs: split on newlines, trim, keep non-empty.
      const wf = (webfontUrls || "")
        .split(/\n+/).map((s) => s.trim()).filter(Boolean);
      patch.webfont_urls = wf.length ? wf : null;
      const { data } = await api.patch(`/master/tenants/${tenant.id}/theme`, patch);
      setTheme(data.theme || {});
      setResolved(data.resolved);
      toast.success("Branding saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset all branding to Quanta defaults? Uploaded files will be deleted.")) return;
    try {
      await api.delete(`/master/tenants/${tenant.id}/theme`);
      toast.success("Reset to Quanta defaults");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-sm text-muted-foreground" data-testid="branding-loading">
        Loading branding…
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4" data-testid="branding-config-panel">
      <SheetHeader>
        <SheetTitle>Branding · {tenant.name}</SheetTitle>
        <SheetDescription>
          Per-workspace theme, logo assets, email sender name, and custom domain.
          Nulls fall back to Quanta defaults.
        </SheetDescription>
      </SheetHeader>

      <Tabs defaultValue="theme" className="flex-1">
        <TabsList data-testid="branding-tabs">
          <TabsTrigger value="theme" data-testid="tab-theme">Theme</TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets">Assets</TabsTrigger>
          <TabsTrigger value="domain" data-testid="tab-domain">Custom Domain</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">Email Delivery</TabsTrigger>
        </TabsList>

        {/* -------- THEME TAB -------- */}
        <TabsContent value="theme" className="mt-4 space-y-6">
          <div className="grid gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="product">Product display name</Label>
              <Input
                id="product"
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                placeholder="Quanta"
                data-testid="branding-product-name"
              />
              <p className="text-xs text-muted-foreground">
                Replaces "Quanta" in UI header + emails when set.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tagline">Tagline</Label>
              <Input
                id="tagline"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                placeholder="Your day, understood."
                data-testid="branding-tagline"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="from-name">Email sender display name</Label>
              <Input
                id="from-name"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder="Acme Time"
                data-testid="branding-from-name"
              />
              <p className="text-xs text-muted-foreground">
                Emails send as <code className="text-[10px]">{fromName || "Quanta"} &lt;no-reply@quantatime.app&gt;</code>.
                The address itself is platform-controlled.
              </p>
            </div>
          </div>

          <Separator />

          <div>
            <div className="mb-2 text-sm font-semibold text-primary">Colors</div>
            <div className="grid grid-cols-2 gap-3">
              {COLOR_KEYS.map((k) => (
                <div key={k} className="space-y-1.5" data-testid={`color-row-${k}`}>
                  <Label htmlFor={`color-${k}`} className="capitalize">{k}</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={colors[k] || (resolved?.colors?.[k] || "#000000")}
                      onChange={(e) => setColors({ ...colors, [k]: e.target.value.toUpperCase() })}
                      className="h-9 w-9 shrink-0 cursor-pointer rounded border"
                      data-testid={`color-picker-${k}`}
                    />
                    <Input
                      id={`color-${k}`}
                      value={colors[k]}
                      placeholder={resolved?.colors?.[k] || "#000000"}
                      onChange={(e) => setColors({ ...colors, [k]: e.target.value })}
                      data-testid={`color-input-${k}`}
                      className="font-mono"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          <div className="grid gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="font-body">Body font family</Label>
              <Input
                id="font-body"
                value={fontBody}
                onChange={(e) => setFontBody(e.target.value)}
                placeholder='"Inter", ui-sans-serif, system-ui'
                data-testid="branding-font-body"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="font-display">Display font family</Label>
              <Input
                id="font-display"
                value={fontDisplay}
                onChange={(e) => setFontDisplay(e.target.value)}
                placeholder='"Space Grotesk", ui-sans-serif'
                data-testid="branding-font-display"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="webfonts">Webfont URLs (one per line)</Label>
              <Textarea
                id="webfonts"
                rows={3}
                value={webfontUrls}
                onChange={(e) => setWebfontUrls(e.target.value)}
                placeholder="https://fonts.googleapis.com/css2?family=Inter"
                data-testid="branding-webfonts"
              />
              <p className="text-xs text-muted-foreground">
                Must be <code>https://</code>. Loaded via <code>&lt;link rel="stylesheet"&gt;</code> at boot.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button onClick={save} disabled={saving} data-testid="branding-save">
              {saving ? "Saving…" : "Save theme"}
            </Button>
            <Button variant="ghost" onClick={resetAll} data-testid="branding-reset">
              Reset to Quanta defaults
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const slug = tenant.slug;
                if (!slug) {
                  toast.error("Workspace URL is missing — can't open preview.");
                  return;
                }
                const url = `/login?tenant_slug=${encodeURIComponent(slug)}&_theme_preview=1`;
                window.open(url, "_blank", "noopener,noreferrer");
              }}
              data-testid="branding-preview-theme"
              title="Opens /login rendered with this workspace's theme in a new tab"
            >
              Preview theme
            </Button>
            {theme.updated_at && (
              <span className="text-xs text-muted-foreground" data-testid="branding-updated-at">
                Last saved {new Date(theme.updated_at).toLocaleString()}
              </span>
            )}
          </div>
        </TabsContent>

        {/* -------- ASSETS TAB -------- */}
        <TabsContent value="assets" className="mt-4 space-y-4">
          <EphemeralWarning />
          {ASSET_SLOTS.map((slot) => (
            <AssetSlot
              key={slot.kind}
              tenantId={tenant.id}
              kind={slot.kind}
              label={slot.label}
              url={theme[`${slot.kind === "logo_primary" ? "logo_primary" :
                          slot.kind === "logo_reversed" ? "logo_reversed" :
                          slot.kind === "logo_symbol" ? "logo_symbol" :
                          slot.kind === "favicon" ? "favicon" :
                          "email_header_banner"}_url`]}
              onDone={load}
            />
          ))}
        </TabsContent>

        {/* -------- DOMAIN TAB -------- */}
        <TabsContent value="domain" className="mt-4 space-y-4">
          <CustomDomainPanel
            tenantId={tenant.id}
            info={customDomain}
            pendingHost={pendingHost}
            setPendingHost={setPendingHost}
            onChanged={load}
          />
        </TabsContent>

        {/* -------- EMAIL DELIVERY TAB (Phase 15) -------- */}
        <TabsContent value="email" className="mt-4 space-y-4">
          <EmailDeliveryPanel tenantId={tenant.id} />
        </TabsContent>
      </Tabs>

      {/* Live mini-preview */}
      <PreviewStrip
        product={product || resolved?.product_display_name || "Quanta"}
        tagline={tagline || resolved?.tagline || ""}
        colors={{
          navy: colors.navy || resolved?.colors?.navy || "#0B2545",
          blue: colors.blue || resolved?.colors?.blue || "#2563EB",
          cyan: colors.cyan || resolved?.colors?.cyan || "#06B6D4",
        }}
        logo={absoluteBrandUrl(theme.logo_primary_url)}
      />

      <div className="pt-2 text-right">
        <Button variant="ghost" onClick={onClose} data-testid="branding-close">Close</Button>
      </div>
    </div>
  );
}


function EphemeralWarning() {
  return (
    <div
      className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
      data-testid="branding-ephemeral-warning"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        Uploads are stored on ephemeral pod storage and will be lost on redeploy.
        For production, self-host assets on your own CDN and paste the HTTPS URL
        in the Theme tab (logo/favicon URL fields).
      </div>
    </div>
  );
}


function AssetSlot({ tenantId, kind, label, url, onDone }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const abs = absoluteBrandUrl(url);

  const onSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", kind);
      await api.post(`/master/tenants/${tenantId}/theme/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${label} uploaded`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.delete(`/master/tenants/${tenantId}/theme/asset?kind=${kind}`);
      toast.success(`${label} cleared`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border bg-white p-3" data-testid={`asset-slot-${kind}`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-medium text-primary">{label}</div>
        {abs && (
          <Button size="sm" variant="ghost" onClick={clear} disabled={busy}
                  data-testid={`asset-clear-${kind}`}>
            <X className="mr-1 h-3 w-3" /> Clear
          </Button>
        )}
      </div>
      <div className="flex items-center gap-3">
        <div className="flex h-14 w-24 items-center justify-center rounded border bg-muted/30 text-xs text-muted-foreground">
          {abs ? (
            <img src={abs} alt="preview" className="max-h-full max-w-full" />
          ) : (
            "no file"
          )}
        </div>
        <input
          type="file"
          ref={inputRef}
          onChange={onSelect}
          accept={kind === "favicon" ? "image/x-icon,image/png,image/svg+xml"
                  : kind === "email_header_banner" ? "image/png,image/jpeg,image/webp"
                  : "image/svg+xml,image/png,image/jpeg,image/webp"}
          className="text-xs"
          data-testid={`asset-input-${kind}`}
          disabled={busy}
        />
      </div>
    </div>
  );
}


function CustomDomainPanel({ tenantId, info, pendingHost, setPendingHost, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [verifyRes, setVerifyRes] = useState(null);

  const setHost = async () => {
    setBusy(true);
    try {
      await api.patch(`/master/tenants/${tenantId}/custom-domain`, {
        host: pendingHost || null,
      });
      toast.success(pendingHost ? "Custom domain set" : "Custom domain cleared");
      setVerifyRes(null);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/master/tenants/${tenantId}/custom-domain/verify`);
      setVerifyRes(data);
      if (data.ok) toast.success("DNS verified");
      else toast.error(`DNS check failed: ${data.error || "unknown"}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="custom-domain-panel">
      <div className="space-y-1.5">
        <Label htmlFor="domain-host">Host</Label>
        <div className="flex gap-2">
          <Input
            id="domain-host"
            value={pendingHost}
            onChange={(e) => setPendingHost(e.target.value)}
            placeholder="time.acme.example"
            data-testid="domain-host-input"
          />
          <Button onClick={setHost} disabled={busy} data-testid="domain-save-btn">
            <Globe className="mr-1.5 h-3.5 w-3.5" /> Save
          </Button>
        </div>
      </div>

      {info?.custom_domain && (
        <div className="rounded-lg border bg-muted/30 p-3 text-sm">
          <div className="mb-2 flex items-center gap-2">
            <Badge variant={info.custom_domain_verified_at ? "default" : "secondary"}
                   data-testid="domain-verified-badge">
              {info.custom_domain_verified_at ? "Verified" : "Unverified"}
            </Badge>
            {info.custom_domain_verified_at && (
              <span className="text-xs text-muted-foreground">
                {new Date(info.custom_domain_verified_at).toLocaleString()}
              </span>
            )}
          </div>
          <div className="space-y-1 font-mono text-xs">
            <div>
              <b>CNAME</b> <code>{info.custom_domain}</code>
              {" → "}
              <code>{window.location.host}</code>
            </div>
            <div>
              <b>TXT</b>{" "}
              <code>{info.expected_txt_record_name}</code>
              {" = "}
              <code>{info.expected_txt_record_value}</code>
            </div>
          </div>
          <div className="mt-3">
            <Button size="sm" onClick={verify} disabled={busy} data-testid="domain-verify-btn">
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Verify DNS
            </Button>
          </div>
          {verifyRes && (
            <pre className="mt-3 max-h-48 overflow-auto rounded bg-white p-2 text-[11px]"
                 data-testid="domain-verify-result">
              {JSON.stringify(verifyRes, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}


function PreviewStrip({ product, tagline, colors, logo }) {
  return (
    <div
      className="mt-4 rounded-xl border p-4"
      style={{ background: colors.canvas || "#F7F9FC" }}
      data-testid="branding-preview"
    >
      <div
        className="flex items-center gap-3 rounded-lg p-3"
        style={{ background: colors.navy, color: "white" }}
      >
        {logo ? (
          <img src={logo} alt="logo" className="h-6" />
        ) : (
          <div className="text-sm font-semibold">{product}</div>
        )}
        <div className="text-xs opacity-80">{tagline}</div>
      </div>
      <div className="mt-2 flex gap-2">
        <button
          className="rounded-md px-3 py-1.5 text-xs font-medium text-white"
          style={{ background: colors.blue }}
        >
          Primary action
        </button>
        <button
          className="rounded-md border px-3 py-1.5 text-xs"
          style={{ borderColor: colors.cyan, color: colors.cyan }}
        >
          Secondary
        </button>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Phase 15 — Email delivery panel: verified sender domain, DNS check,
// last 20 fallback events.
// -------------------------------------------------------------------------

function EmailDeliveryPanel({ tenantId }) {
  const [emailCfg, setEmailCfg] = useState({});
  const [domain, setDomain] = useState("");
  const [saving, setSaving] = useState(false);
  const [dnsBusy, setDnsBusy] = useState(false);
  const [dns, setDns] = useState(null);
  const [fallbacks, setFallbacks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [tenantResp, fbResp] = await Promise.all([
        api.get(`/master/tenants`),
        api.get(`/master/tenants/${tenantId}/mail-fallbacks`),
      ]);
      const items = tenantResp.data?.items ?? tenantResp.data ?? [];
      const t = items.find((x) => x.id === tenantId) || {};
      const cfg = t.email || {};
      setEmailCfg(cfg);
      setDomain(cfg.verified_sender_domain || "");
      setFallbacks(fbResp.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  const save = async () => {
    setSaving(true);
    setDns(null);
    try {
      await api.patch(`/master/tenants/${tenantId}/email`, {
        verified_sender_domain: (domain && domain.trim()) || null,
      });
      toast.success(domain ? "Verified sender saved" : "Reverted to platform sender");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const runDnsCheck = async () => {
    setDnsBusy(true);
    try {
      const { data } = await api.post(`/master/tenants/${tenantId}/email/dns-check`);
      setDns(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDnsBusy(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground" data-testid="email-delivery-loading">Loading email delivery…</div>;
  }

  const allBad = dns && !dns.overall_ok;

  return (
    <div className="space-y-6" data-testid="email-delivery-panel">
      <div className="space-y-2">
        <Label htmlFor="verified-sender">Verified sender domain</Label>
        <div className="flex gap-2">
          <Input
            id="verified-sender"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="mail.acme.com"
            data-testid="email-verified-domain-input"
          />
          <Button onClick={save} disabled={saving} data-testid="email-save-domain-btn">
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            Save
          </Button>
          <Button
            variant="outline"
            onClick={runDnsCheck}
            disabled={dnsBusy || !emailCfg.verified_sender_domain}
            data-testid="email-dns-check-btn"
          >
            {dnsBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            Check DNS
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Send emails as <code>{`{display_name} <no-reply@${domain || "quantatime.app"}>`}</code>. Requires SPF, DKIM, DMARC pointing at Resend.
        </p>
      </div>

      {allBad && (
        <div
          className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-900"
          data-testid="email-fallback-warning"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4" />
          <div>
            <strong>Emails will fall back to the platform sender</strong> until the DNS rows below turn green. Existing traffic keeps flowing — no message is dropped.
          </div>
        </div>
      )}

      {dns && (
        <div className="rounded-xl border p-3 space-y-2" data-testid="email-dns-result-card">
          <div className="mb-1 flex items-center justify-between">
            <div className="text-sm font-semibold text-primary">DNS check · <code className="text-[11px]">{dns.domain}</code></div>
            <Badge
              variant="outline"
              className={dns.overall_ok ? "rounded-full border-emerald-500/40 text-emerald-700" : "rounded-full border-red-500/40 text-red-700"}
              data-testid="email-dns-overall"
            >
              {dns.overall_ok ? "All good" : "Action required"}
            </Badge>
          </div>
          <DnsRow label="SPF" result={dns.spf} testid="email-dns-row-spf" />
          <DnsRow label="DKIM" result={dns.dkim} testid="email-dns-row-dkim" />
          <DnsRow label="DMARC" result={dns.dmarc} testid="email-dns-row-dmarc" />
          <p className="pt-1 text-[11px] text-muted-foreground">
            Checked at {new Date(dns.checked_at).toLocaleString()}.{" "}
            <a
              className="underline"
              href="https://resend.com/docs/dashboard/domains/introduction"
              target="_blank" rel="noreferrer"
            >
              How to set up SPF / DKIM / DMARC for Resend →
            </a>
          </p>
        </div>
      )}

      <div className="rounded-xl border p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-primary">
          <MailWarning className="h-4 w-4" /> Recent fallback events
          <Badge variant="outline" className="rounded-full text-[10px]" data-testid="email-fallback-count">
            {fallbacks.length}
          </Badge>
        </div>
        {fallbacks.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="email-fallback-empty">
            No fallbacks recorded. Emails are sending from the configured sender.
          </p>
        ) : (
          <ul className="divide-y text-xs" data-testid="email-fallback-list">
            {fallbacks.map((f) => (
              <li key={f.id} className="py-2" data-testid={`email-fallback-row-${f.id}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <div>
                    <span className="font-mono text-[11px] text-muted-foreground">{f.to}</span>
                    <span className="mx-1 text-muted-foreground">·</span>
                    <span>{f.subject}</span>
                  </div>
                  <span className="tabular-nums text-[11px] text-muted-foreground">
                    {new Date(f.timestamp).toLocaleString()}
                  </span>
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  intended: <code>{f.intended_from}</code>
                  <span className="mx-1">→</span>
                  sent as: <code>{f.actual_from}</code>
                  <span className="ml-1 rounded bg-amber-500/10 px-1 text-amber-800">{f.reason}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function DnsRow({ label, result, testid }) {
  if (!result) return null;
  const ok = !!result.ok;
  return (
    <div className="flex items-start gap-2 rounded-md border p-2" data-testid={testid}>
      {ok ? (
        <ShieldCheck className="mt-0.5 h-4 w-4 text-emerald-600" />
      ) : (
        <ShieldAlert className="mt-0.5 h-4 w-4 text-red-600" />
      )}
      <div className="flex-1">
        <div className="flex items-center gap-2 text-sm font-medium">
          {label}
          <Badge
            variant="outline"
            className={ok ? "rounded-full border-emerald-500/40 text-emerald-700" : "rounded-full border-red-500/40 text-red-700"}
          >
            {ok ? "verified" : (result.error || "missing")}
          </Badge>
        </div>
        {(result.records || []).map((r, i) => (
          <div key={i} className="mt-0.5 break-all font-mono text-[11px] text-muted-foreground">
            {r}
          </div>
        ))}
      </div>
    </div>
  );
}

